/**
 * Store des foyers — port direct du proto (Phase 1, incrément 3).
 *
 * Reproduit le comportement de `HOMES` / `currentHomeId` du prototype
 * (`docs/legacy-prototype/nestor_prod.html` l. 821, 962, 982, 1040+) avec
 * les MÊMES clés `localStorage` (`nestor_homes`) et le même schéma JSON
 * pour permettre la coexistence si jamais on charge l'ancien proto et
 * la nouvelle app dans le même navigateur. Pas de migration de schéma
 * tant que l'incrément 2 (Phase 2 du roadmap) n'a pas livré IndexedDB.
 *
 * Décisions architecturales :
 *   - `data.photos[].img` est sérialisé en `'[base64]'` placeholder pour
 *     ne pas saturer localStorage (5 Mo) — fidèle à `saveHomes()` du
 *     proto. Les blobs réels vivent dans `nestor_data` (Phase 2 = IDB).
 *   - Le mode du foyer courant pilote la palette UI via
 *     `body[data-mode]`. C'est le store qui notifie ; l'AppShell s'abonne.
 *   - `getCurrentHome()` retourne `null` si aucun foyer — l'UI doit
 *     gérer ce cas (login/onboarding non encore portés).
 */
import type {
  Album,
  BagCategory,
  BagItem,
  ClassicHomeData,
  CoparentingHomeData,
  CpActivity,
  CustodyConfig,
  DemandeStatus,
  Expense,
  ExpenseCategory,
  FamilyEvent,
  FamilyMessage,
  FamilyTask,
  GroceryItem,
  Home,
  HomeColor,
  HomeData,
  HomeId,
  HomeMode,
  JournalEntry,
  LiaisonStatus,
  Meal,
  MealRecipeInfo,
  Member,
  MemberId,
  PersonId,
  Photo,
  Role,
  SchoolEntry,
  Transmission,
} from '@nestor/shared-types';
import { createStore } from '../lib/store.js';
import { getLang } from '../i18n/index.js';
import { deleteBlob, putBlob } from '../storage/blobs.js';
import { clearPhotoUrl, setPhotoUrl } from './photoUrls.js';
import { clearVoiceUrl, setVoiceUrl } from './voiceUrls.js';

export const STORAGE_KEY_HOMES = 'nestor_homes';

/**
 * Sentinel placé dans `photo.img` (et depuis 2.3 dans `message.audioUrl`)
 * quand le blob réel vit dans IndexedDB sous une clé typée
 * (`photo:<id>`, `voice:<msgId>`, etc.). À l'affichage, la View passe
 * par un cache mémoire (`getPhotoUrlNow` / `getVoiceUrlNow`) qui
 * hydrate depuis IDB au premier appel.
 *
 * Pourquoi un sentinel et pas juste `null` ? Parce que `null` signifie
 * « pas de média » (fallback UI), alors que `'[idb]'` signifie « média
 * réel, à charger ». Distinction utile pour le rendu — et même
 * sentinel pour photos et voice, mêmes sémantiques.
 */
export const PHOTO_IDB_SENTINEL = '[idb]';
/** Alias explicite pour les voice messages — même chaîne, sens identique. */
export const VOICE_IDB_SENTINEL = PHOTO_IDB_SENTINEL;

interface HomesState {
  homes: Home[];
  currentHomeId: HomeId;
}

// ── Default factories (port direct du proto) ──────────────────────────

/** Libellés courts des jours selon la langue, alignés sur le proto l. 810. */
function dayLabels(): readonly [string, string, string, string, string, string, string] {
  const lang = getLang();
  if (lang === 'en') return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  if (lang === 'es') return ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  // Proto ne fournit que fr/en/es ; les autres langues retombent sur fr
  return ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
}

export function defaultMeals(): Meal[] {
  return dayLabels().map((d, i) => ({
    d,
    m: '',
    tag: '',
    today: i === 0,
    bs: 4,
    ing: [],
  }));
}

export function defaultClassicData(): ClassicHomeData {
  return {
    events: [],
    tasks: [],
    messages: [],
    meals: defaultMeals(),
    grocery: [],
    photos: [],
    albums: [],
    mealHistory: [],
    members: [],
  };
}

export function defaultCoparentingData(): CoparentingHomeData {
  return {
    journal: [],
    activities: [],
    school: [],
    liaison: [],
    frais: [],
    // B4 (audit, choix Léopold) : un foyer neuf démarre VIERGE — les
    // empty states font l'accueil, pas des données de démonstration
    // non identifiées (Doudou / Sac de sport / Cahier de devoirs).
    bag: [],
    handovers: [],
    custody: {
      type: 'weekly',
      parentA: 'Parent A',
      parentB: 'Parent B',
      startDate: new Date().toISOString().slice(0, 10),
      weekParity: 0,
    },
  };
}

const COLOR_BY_MODE: Readonly<Record<HomeMode, HomeColor>> = {
  classic: '#7A9E8E',
  coparenting: '#5B6C8F',
};

function defaultDataForMode(mode: HomeMode): HomeData {
  return mode === 'coparenting' ? defaultCoparentingData() : defaultClassicData();
}

// ── Persistence (clés et format identiques au proto) ──────────────────

interface SerializedHomesPayload {
  homes: Home[];
  currentHomeId: HomeId;
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? globalThis.localStorage
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Allège les photos pour éviter quota localStorage exceeded.
 *
 * Phase 1 : les blobs base64 étaient remplacés par `'[base64]'` à
 * l'écriture et restaurés `null` au chargement — donnée perdue à
 * chaque reload.
 *
 * Phase 2.2 : les vraies photos vivent dans IndexedDB sous
 * `photo:<id>`. Le champ `photo.img` du store porte désormais le
 * sentinel `'[idb]'` qui indique « blob réel persistant en IDB ».
 *
 * Cette fonction :
 *   - Garde `'[idb]'` tel quel à l'écriture (sentinel léger).
 *   - Convertit toute valeur non-sentinel (data:, blob:, autre) en
 *     `'[idb]'` AVANT l'écriture — un blob: URL n'a pas de sens en
 *     localStorage (durée de vie = document) et une data: URL
 *     consomme du quota pour rien (la vraie copie est en IDB).
 *   - Préserve `null` (pas de photo associée).
 */
function serializeHomes(homes: Home[]): Home[] {
  return homes.map((h) => {
    if (h.mode !== 'classic') return h;
    const data = h.data as ClassicHomeData;
    return {
      ...h,
      data: {
        ...data,
        photos: data.photos.map((p) => ({
          ...p,
          img: p.img == null ? null : PHOTO_IDB_SENTINEL,
        })),
        // Phase 2.3 : `audioUrl` suit la même règle que `photo.img` —
        // toute valeur non-sentinel (blob: URL volatile, data: URL
        // verbeuse) est écrasée en `'[idb]'` avant écriture
        // localStorage. Les bytes réels sont en IDB sous `voice:<id>`.
        // `attachmentUrl` reste tel quel (Phase 1 — migration éventuelle
        // en 2.5 si quota localStorage devient un problème).
        messages: data.messages.map((m) => {
          if (m.audioUrl == null) return m;
          return { ...m, audioUrl: VOICE_IDB_SENTINEL };
        }),
      },
    };
  });
}

/**
 * Restore les photos à la lecture du storage.
 *   - `'[idb]'` reste tel quel — la View le détectera et hydratera
 *     via `getPhotoUrlNow(id)` (cf. `photoUrls.ts`).
 *   - `'[base64]'` (legacy Phase 1) → `null` : la donnée n'a jamais
 *     été persistée, on bascule sur le fallback emoji.
 *   - Tout autre valeur (`data:`, `blob:`, normalement absent du
 *     storage mais on est défensif) reste comme tel.
 */
// Migration B2 (audit) : les identifiants de rôles du proto étaient
// FR (`ado` / `enfant`) — le modèle §2.2 impose l'EN stable (`teen` /
// `child`, aligné sur la table `memberships` API). Les données locales
// existantes sont migrées **à la lecture** ; la prochaine écriture
// persiste les nouveaux identifiants. Migration conservée tant que des
// appareils alpha peuvent porter l'ancien format.
const LEGACY_ROLE_MAP: Readonly<Record<string, Role>> = {
  ado: 'teen',
  enfant: 'child',
};

function migrateLegacyRole(role: string): Role {
  return LEGACY_ROLE_MAP[role] ?? (role as Role);
}

function deserializeHomes(homes: Home[]): Home[] {
  return homes.map((h) => {
    if (h.mode !== 'classic') return h;
    const data = h.data as ClassicHomeData;
    return {
      ...h,
      data: {
        ...data,
        members: data.members.map((m) => ({
          ...m,
          role: migrateLegacyRole(m.role as string),
        })),
        photos: data.photos.map((p) => ({
          ...p,
          img: p.img === '[base64]' ? null : p.img,
        })),
      },
    };
  });
}

function loadFromStorage(storage: Storage | undefined): HomesState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY_HOMES);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SerializedHomesPayload>;
    if (!parsed?.homes?.length) return null;
    const homes = deserializeHomes(parsed.homes);
    const currentHomeId = parsed.currentHomeId ?? homes[0]?.id ?? '';
    return { homes, currentHomeId };
  } catch {
    return null;
  }
}

function persistToStorage(storage: Storage | undefined, state: HomesState): void {
  if (!storage) return;
  try {
    const payload: SerializedHomesPayload = {
      homes: serializeHomes(state.homes),
      currentHomeId: state.currentHomeId,
    };
    storage.setItem(STORAGE_KEY_HOMES, JSON.stringify(payload));
  } catch (err) {
    // Quota exceeded ou storage indisponible : on n'interrompt pas l'UI.
    // En Phase 2 (IndexedDB), ce cas disparaît.
    console.warn('[homes] persist failed:', err);
  }
}

// ── Store ─────────────────────────────────────────────────────────────

const initialState: HomesState = loadFromStorage(safeLocalStorage()) ?? {
  homes: [],
  currentHomeId: '',
};

const homesStore = createStore<HomesState>(initialState);

// Persistance automatique à chaque mutation. Le proto faisait ça
// explicitement à chaque call site (saveHomes()), mais comme tout passe
// désormais par le store, on centralise ici.
homesStore.subscribe((state) => {
  persistToStorage(safeLocalStorage(), state);
});

// ── Sync helpers (Phase 4.5) ──────────────────────────────────────────

/**
 * Détecte si un id de foyer ressemble à un UUID v4 serveur.
 *
 * Les ids locaux (`home_<base36>_<base36>`) et les ids serveur (UUIDs)
 * cohabitent dans le même champ `id`. Cette détection alimente la
 * stratégie « server-wins » d'ADR 0009 : les ids non-UUID sont des
 * foyers pré-auth à migrer ; les UUIDs sont des foyers déjà connus
 * du serveur, donc autorité.
 */
const SERVER_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isServerHomeId(id: HomeId): boolean {
  return SERVER_ID_REGEX.test(id);
}

/**
 * Snapshot brut du store — utile au module sync pour décider de la
 * stratégie avant de muter. Ne pas modifier le tableau retourné.
 */
export function getHomesSnapshot(): { homes: readonly Home[]; currentHomeId: HomeId } {
  const { homes, currentHomeId } = homesStore.getState();
  return { homes, currentHomeId };
}

/**
 * Payload d'un foyer tel que retourné par `GET /homes` — assez de
 * champs pour faire le merge. On ne dépend pas du type `ApiHome` ici
 * pour garder `stores/homes.ts` indépendant de la couche `api/`.
 */
export interface ServerHomePayload {
  id: string;
  name: string;
  mode: HomeMode;
  color: string | null;
}

export interface SyncResult {
  /** Mapping localId → serverId pour les foyers migrés via POST. */
  idMap?: ReadonlyMap<string, string>;
  /** Liste complète des foyers retournée par le serveur (autorité). */
  serverHomes?: readonly ServerHomePayload[];
}

/**
 * Applique en une seule transaction le résultat de la sync (ADR 0009) :
 *
 *   1. Remap des ids locaux migrés via POST → UUID serveur.
 *   2. Merge server-wins : pour chaque foyer serveur, on écrase
 *      `name` + `color` localement (data préservé). Foyer présent
 *      côté serveur mais absent localement → ajouté avec data
 *      par défaut. Foyer avec id UUID absent du serveur → supprimé.
 *      Foyer avec id non-UUID (migration ratée) → conservé tel quel.
 *   3. `currentHomeId` remappé si concerné, fallback sur premier
 *      foyer restant si invalidé.
 *
 * Une seule `setState` → un seul render UI, pas de flicker.
 */
export function applySyncResult(result: SyncResult): void {
  const { homes: localHomes, currentHomeId } = homesStore.getState();
  const idMap = result.idMap ?? new Map<string, string>();
  const serverHomes = result.serverHomes;

  // 1. Remap : reprend les homes locaux et remplace les ids migrés.
  const remappedLocal: Home[] = localHomes.map((h) =>
    idMap.has(h.id) ? { ...h, id: idMap.get(h.id)! } : h,
  );

  // Si on n'a pas de liste serveur (ex. fetch a échoué après une
  // migration réussie), on s'arrête après le remap pour ne pas perdre
  // les foyers qu'on vient de migrer.
  if (!serverHomes) {
    const nextCurrent = idMap.get(currentHomeId) ?? currentHomeId;
    homesStore.setState({ homes: remappedLocal, currentHomeId: nextCurrent });
    return;
  }

  // 2. Merge server-wins.
  const localById = new Map(remappedLocal.map((h) => [h.id, h]));
  const next: Home[] = [];

  for (const sh of serverHomes) {
    const existing = localById.get(sh.id);
    if (existing) {
      // Server wins pour name + color. Data local préservé.
      next.push({
        ...existing,
        name: sh.name,
        color: (sh.color ?? COLOR_BY_MODE[sh.mode]) as HomeColor,
      });
    } else {
      // Nouveau foyer côté serveur (autre appareil, etc.) — on
      // initialise un Home minimal côté local avec data par défaut.
      next.push({
        id: sh.id,
        name: sh.name,
        mode: sh.mode,
        color: (sh.color ?? COLOR_BY_MODE[sh.mode]) as HomeColor,
        data: defaultDataForMode(sh.mode),
      });
    }
  }

  // 3. Conserve les foyers locaux non-UUID (migration ratée) — ils
  //    seront retentés au prochain sync.
  const serverIds = new Set(serverHomes.map((s) => s.id));
  for (const h of remappedLocal) {
    if (!isServerHomeId(h.id) && !serverIds.has(h.id)) {
      next.push(h);
    }
  }

  // 4. currentHomeId : remap d'abord, puis valide qu'il existe encore
  //    dans la liste finale ; sinon premier foyer ou ''.
  const remappedCurrent = idMap.get(currentHomeId) ?? currentHomeId;
  const currentStillValid = next.some((h) => h.id === remappedCurrent);
  const nextCurrent = currentStillValid ? remappedCurrent : (next[0]?.id ?? '');

  homesStore.setState({ homes: next, currentHomeId: nextCurrent });
}

// ── Events sync helpers (Phase 4.6) ───────────────────────────────────

/**
 * Payload event tel que retourné par `GET /homes/:homeId/events` —
 * sous-ensemble des champs nécessaires au merge local. On évite la
 * dépendance vers `api/events.ts` pour garder `stores/homes.ts`
 * indépendant.
 */
export interface ServerEventPayload {
  id: string;
  title: string;
  date: string;
  time: string | null;
  location: string | null;
}

export interface EventsSyncResult {
  /** Mapping localEventId → serverEventId pour migrations POST. */
  idMap?: ReadonlyMap<string, string>;
  /** Liste serveur autoritaire pour ce home. */
  serverEvents?: readonly ServerEventPayload[];
}

/**
 * Applique le résultat de la sync events pour un foyer donné
 * (ADR 0009 étendu aux sub-entités) :
 *
 *   1. Remap des events migrés via POST → UUID serveur.
 *   2. Server-wins : pour chaque event serveur, on écrase
 *      title/date/time/location en local. Event présent serveur
 *      mais absent local → ajouté. Event local avec UUID id
 *      absent du serveur → supprimé. Event local avec id non-UUID
 *      (migration ratée) → conservé.
 *
 * No-op si le foyer n'existe pas localement ou n'est pas en mode
 * classique (les events sont une entité classic-only — le
 * co-parentage a `activities` à la place, qui aura sa propre sync
 * en 4.7+).
 *
 * Une seule `setState` → atomic, pas de flicker UI.
 */
export function applyEventsSyncResult(homeId: HomeId, result: EventsSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return;

  const data = home.data as ClassicHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverEvents = result.serverEvents;

  // 1. Remap des ids locaux migrés.
  const remappedLocal: FamilyEvent[] = data.events.map((e) =>
    idMap.has(e.id) ? { ...e, id: idMap.get(e.id)! } : e,
  );

  if (!serverEvents) {
    // Pas de liste serveur (fetch échoué après migration réussie) — on
    // s'arrête après le remap pour ne pas perdre les events migrés.
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, events: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  // 2. Merge server-wins.
  const localById = new Map(remappedLocal.map((e) => [e.id, e]));
  const nextEvents: FamilyEvent[] = [];

  for (const se of serverEvents) {
    const existing = localById.get(se.id);
    const merged: FamilyEvent = {
      id: se.id,
      title: se.title,
      date: se.date,
      ...(se.time ? { time: se.time } : {}),
      ...(se.location ? { location: se.location } : {}),
      // `who` est un MemberId local — non synchronisé en 4.6 (les
      // membres restent locaux). On préserve la valeur locale si
      // elle existait.
      ...(existing?.who ? { who: existing.who } : {}),
    };
    nextEvents.push(merged);
  }

  // 3. Conserve les events locaux non-UUID (migration ratée) — retentés
  //    au prochain sync.
  const serverIds = new Set(serverEvents.map((s) => s.id));
  for (const e of remappedLocal) {
    if (!isServerHomeId(e.id) && !serverIds.has(e.id)) {
      nextEvents.push(e);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, events: nextEvents } };
  homesStore.setState({ homes: next });
}

// ── Tasks sync helpers (Phase 4.7) ────────────────────────────────────

/**
 * Payload task tel que retourné par `GET /homes/:homeId/tasks` —
 * sous-ensemble des champs nécessaires au merge local. Indépendant
 * de `api/tasks.ts` pour ne pas créer de dépendance circulaire.
 */
export interface ServerTaskPayload {
  id: string;
  text: string;
  due: string | null;
  done: boolean;
}

export interface TasksSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverTasks?: readonly ServerTaskPayload[];
}

/**
 * Applique le résultat de la sync tasks pour un foyer donné —
 * symétrique de `applyEventsSyncResult` (4.6).
 *
 *   1. Remap des ids locaux migrés via POST.
 *   2. Server-wins par task : `text` / `due` / `done` écrasés. Le
 *      champ local `who` (MemberId, non synchronisé en 4.7) est
 *      préservé.
 *   3. Tasks UUID absentes serveur → supprimées localement.
 *   4. Tasks locales non-UUID (migration ratée) → conservées.
 *
 * No-op si le foyer n'existe pas ou n'est pas en mode classique.
 */
export function applyTasksSyncResult(homeId: HomeId, result: TasksSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return;

  const data = home.data as ClassicHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverTasks = result.serverTasks;

  const remappedLocal: FamilyTask[] = data.tasks.map((t) =>
    idMap.has(t.id) ? { ...t, id: idMap.get(t.id)! } : t,
  );

  if (!serverTasks) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, tasks: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const localById = new Map(remappedLocal.map((t) => [t.id, t]));
  const nextTasks: FamilyTask[] = [];

  for (const st of serverTasks) {
    const existing = localById.get(st.id);
    const merged: FamilyTask = {
      id: st.id,
      text: st.text,
      done: st.done,
      ...(st.due ? { due: st.due } : {}),
      // `who` préservé du local (MemberId, non synchronisé).
      ...(existing?.who ? { who: existing.who } : {}),
    };
    nextTasks.push(merged);
  }

  const serverIds = new Set(serverTasks.map((s) => s.id));
  for (const t of remappedLocal) {
    if (!isServerHomeId(t.id) && !serverIds.has(t.id)) {
      nextTasks.push(t);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, tasks: nextTasks } };
  homesStore.setState({ homes: next });
}

// ── Messages sync helpers (Phase 4.8 texte + 4.11 audio R2) ───────────

/**
 * Payload message tel que retourné par `GET /homes/:homeId/messages`.
 *
 * **Phase 4.8** : texte seul.
 * **Phase 4.11** : étendu avec `mediaKey` / `audioUrl` pour les
 * voicemails. `text` devient nullable côté serveur (un voicemail
 * audio-only n'a pas de texte). `audioUrl` est une presigned GET
 * R2 ingérée immédiatement dans le cache `voiceUrls`.
 */
export interface ServerMessagePayload {
  id: string;
  text: string | null;
  mediaKey: string | null;
  audioUrl: string | null;
  createdAt: string;
}

export interface MessagesSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverMessages?: readonly ServerMessagePayload[];
}

/**
 * Détecte si un message local est éligible à la **migration POST
 * texte simple** (4.8). En 4.11, les voicemails locaux pré-auth ne
 * sont toujours pas migrés automatiquement — ils nécessitent un
 * upload R2 explicite via `uploadVoicemail` côté UI. Ce filtre
 * reste donc utile pour le path de migration POST.
 *
 * Les messages reçus depuis le serveur (GET) avec `audioUrl` non
 * null sont en revanche bien ingérés au merge — le sentinel
 * `'[idb]'` est posé sur `audioUrl` côté state, l'URL réelle vit
 * dans le cache `voiceUrls`.
 */
export function isTextOnlyMessage(m: FamilyMessage): boolean {
  return Boolean(m.text) && m.audioUrl == null && m.attachmentUrl == null;
}

/**
 * Applique le résultat de la sync messages pour un foyer.
 *
 * Particularités vs events/tasks :
 *
 *   - **Messages avec audio venant du serveur (4.11)** : `audioUrl`
 *     ingéré immédiatement dans le cache `voiceUrls` (rendu sans
 *     round-trip), state local conserve `audioUrl: VOICE_IDB_SENTINEL`
 *     comme pour photos.
 *   - **Messages locaux pré-auth avec audio** : conservés tels
 *     quels (la migration audio nécessite un upload R2 explicite,
 *     pas fait automatiquement).
 *   - **Champ local `at` (number)** dérivé de `createdAt` ISO
 *     serveur : `Date.parse(createdAt)`.
 *   - **`who` (MemberId)** préservé comme pour events/tasks.
 *
 * Le merge garde l'ordre tel quel — le tri newest-first est
 * appliqué au render par les vues qui consomment `data.messages`.
 */
export function applyMessagesSyncResult(homeId: HomeId, result: MessagesSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return;

  const data = home.data as ClassicHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverMessages = result.serverMessages;

  // 1. Remap ids locaux migrés.
  const remappedLocal: FamilyMessage[] = data.messages.map((m) =>
    idMap.has(m.id) ? { ...m, id: idMap.get(m.id)! } : m,
  );

  if (!serverMessages) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, messages: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const localById = new Map(remappedLocal.map((m) => [m.id, m]));
  const nextMessages: FamilyMessage[] = [];

  // 2. Server-wins par message UUID. Texte et/ou audio.
  for (const sm of serverMessages) {
    const existing = localById.get(sm.id);
    const merged: FamilyMessage = {
      id: sm.id,
      // `who` requis dans le type FamilyMessage — on tombe sur
      // l'existant local, ou un fallback synthétique si absent.
      // Phase 4.9+ ajoutera un champ user_id côté serveur qui
      // remplacera ce fallback.
      who: existing?.who ?? 'unknown',
      at: Date.parse(sm.createdAt),
      ...(sm.text ? { text: sm.text } : {}),
      // 4.11 : si le serveur expose un audioUrl (= mediaKey non null),
      // on pose le sentinel côté state et on ingère l'URL dans le
      // cache voiceUrls pour un rendu immédiat. Cohérent avec photos.
      ...(sm.mediaKey ? { audioUrl: VOICE_IDB_SENTINEL } : {}),
    };
    nextMessages.push(merged);
    if (sm.audioUrl) {
      setVoiceUrl(sm.id, sm.audioUrl);
    }
  }

  // 3. Conserve les messages locaux non-UUID (= pré-auth pas
  //    encore migrés) ET tous les messages locaux **avec media**
  //    (la migration audio nécessite un upload R2 explicite, pas
  //    fait par ce sync — elle se fait via `uploadVoicemail` côté UI).
  const serverIds = new Set(serverMessages.map((s) => s.id));
  for (const m of remappedLocal) {
    if (!serverIds.has(m.id)) {
      const isLocalOnly = !isServerHomeId(m.id);
      const isMedia = !isTextOnlyMessage(m);
      if (isLocalOnly || isMedia) {
        nextMessages.push(m);
      }
      // Sinon (UUID, texte-only, absent serveur) → supprimé
      // (server-wins : a été supprimé ailleurs).
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, messages: nextMessages } };
  homesStore.setState({ homes: next });
}

// ── Photos sync helpers (Phase 4.9) ───────────────────────────────────

/**
 * Payload photo tel que retourné par `GET /homes/:homeId/photos`.
 * On y reçoit l'URL presigned R2 (15 min TTL) qui sera mise en
 * cache par `photoUrls.ts` pour le render UI.
 */
export interface ServerPhotoPayload {
  id: string;
  /** URL presigned GET R2 — peut être `null` si R2 down momentanément. */
  url: string | null;
  caption: string | null;
  albumName: string | null;
  takenAt: string | null;
}

export interface PhotosSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverPhotos?: readonly ServerPhotoPayload[];
}

/**
 * Détecte si une photo locale est uploadable côté API 4.9 — i.e.
 * elle a un blob réel persistant en IDB (sentinel `'[idb]'`).
 * Les photos avec `img: null` (placeholder fallback emoji) ou
 * d'autres valeurs (legacy data: URLs) sont skipées.
 */
export function isUploadablePhoto(p: Photo): boolean {
  return p.img === PHOTO_IDB_SENTINEL;
}

/**
 * Applique le résultat de la sync photos pour un foyer.
 *
 * Particularités vs events/tasks/messages :
 *
 *   - **`img` reste sentinel `[idb]`** pour toutes les photos —
 *     les blobs réels vivent désormais ailleurs (IDB local pour
 *     les photos pré-auth, R2 pour celles synchronisées). Le
 *     cache mémoire `photoUrls.ts` route entre les deux.
 *   - **`url` serveur ingérée** dans le cache `photoUrls`
 *     (`setPhotoUrl(id, url)`) — le View hydrate immédiatement
 *     au prochain render sans toucher IDB.
 *   - **`caption` / `album` / `takenAt`** écrasés par valeurs serveur.
 *   - **Photos UUID absentes serveur** : supprimées localement
 *     (server-wins). Le blob IDB associé est purgé.
 */
export function applyPhotosSyncResult(homeId: HomeId, result: PhotosSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return;

  const data = home.data as ClassicHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverPhotos = result.serverPhotos;

  // 1. Remap ids locaux migrés.
  const remappedLocal: Photo[] = data.photos.map((p) =>
    idMap.has(p.id) ? { ...p, id: idMap.get(p.id)! } : p,
  );

  if (!serverPhotos) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, photos: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const localById = new Map(remappedLocal.map((p) => [p.id, p]));
  const nextPhotos: Photo[] = [];

  // 2. Server-wins par photo.
  for (const sp of serverPhotos) {
    const existing = localById.get(sp.id);
    const merged: Photo = {
      id: sp.id,
      img: PHOTO_IDB_SENTINEL,
      ...(sp.caption ? { caption: sp.caption } : {}),
      ...(sp.albumName ? { album: sp.albumName } : {}),
      ...(sp.takenAt
        ? { takenAt: Date.parse(sp.takenAt) }
        : existing?.takenAt
          ? { takenAt: existing.takenAt }
          : {}),
    };
    nextPhotos.push(merged);
    // Cache mémoire pour rendu immédiat. Si URL est null (R2
    // temporairement KO), on saute — le fallback emoji s'affichera
    // jusqu'au prochain sync.
    if (sp.url) {
      setPhotoUrl(sp.id, sp.url);
    }
  }

  // 3. Conserve les photos locales non-UUID (migration ratée) ET
  //    les photos locales sans blob IDB (fallback). Les photos
  //    UUID texte avec blob IDB absentes serveur sont supprimées
  //    (server-wins) + purge IDB.
  const serverIds = new Set(serverPhotos.map((s) => s.id));
  for (const p of remappedLocal) {
    if (serverIds.has(p.id)) continue;
    if (!isServerHomeId(p.id)) {
      // Pré-auth pas encore migrée → on garde.
      nextPhotos.push(p);
    } else {
      // UUID absente serveur → supprimée. Nettoyage cache + IDB.
      clearPhotoUrl(p.id);
      void deleteBlob(`photo:${p.id}`);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, photos: nextPhotos } };
  homesStore.setState({ homes: next });
}

// ── Journal sync helpers (Phase 4.10.1) ───────────────────────────────

/**
 * Payload journal tel que retourné par `GET /homes/:homeId/journal`.
 * Sous-entité **coparenting**. Pattern symétrique aux messages (4.8) :
 * texte + created_at, `who` non synchronisé.
 */
export interface ServerJournalEntryPayload {
  id: string;
  text: string;
  createdAt: string;
}

export interface JournalSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverEntries?: readonly ServerJournalEntryPayload[];
}

/**
 * Applique le résultat de la sync journal pour un foyer coparenting.
 *
 * Particularités vs sub-entités classiques (events/tasks) :
 *   - Mute `home.data.journal` au lieu de `data.events` / `data.tasks`.
 *   - Filtre `home.mode === 'coparenting'` (no-op sinon).
 *   - `who` (MemberId local) préservé comme partout ; `at: number`
 *     dérivé de `Date.parse(createdAt)` comme pour messages.
 */
export function applyJournalSyncResult(homeId: HomeId, result: JournalSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return;

  const data = home.data as CoparentingHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverEntries = result.serverEntries;

  // 1. Remap ids locaux migrés.
  const remappedLocal: JournalEntry[] = data.journal.map((e) =>
    idMap.has(e.id) ? { ...e, id: idMap.get(e.id)! } : e,
  );

  if (!serverEntries) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, journal: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const localById = new Map(remappedLocal.map((e) => [e.id, e]));
  const nextEntries: JournalEntry[] = [];

  // 2. Server-wins par entry.
  for (const se of serverEntries) {
    const existing = localById.get(se.id);
    const merged: JournalEntry = {
      id: se.id,
      at: Date.parse(se.createdAt),
      text: se.text,
      ...(existing?.who ? { who: existing.who } : {}),
    };
    nextEntries.push(merged);
  }

  // 3. Conserve les entries locales non-UUID (migration ratée).
  const serverIds = new Set(serverEntries.map((s) => s.id));
  for (const e of remappedLocal) {
    if (!isServerHomeId(e.id) && !serverIds.has(e.id)) {
      nextEntries.push(e);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, journal: nextEntries } };
  homesStore.setState({ homes: next });
}

// ── Liaison sync helpers (Phase 4.10.2) ───────────────────────────────

/**
 * Payload transmission tel que retourné par `GET /homes/:homeId/liaison`.
 * Sous-entité **coparenting**. Diffère du journal (4.10.1) par la
 * machine d'état : `status` et `signatureRequested` font partie du
 * merge server-wins (statut mutable côté serveur via `/sign` et PATCH).
 */
export interface ServerTransmissionPayload {
  id: string;
  kind: 'transmission' | 'demande';
  text: string;
  signatureRequested: boolean;
  status: string;
  createdAt: string;
}

export interface LiaisonSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverTransmissions?: readonly ServerTransmissionPayload[];
}

/**
 * Applique le résultat de la sync liaison pour un foyer coparenting.
 *
 * Particularités vs journal (4.10.1) :
 *   - `status` et `signatureRequested` participent au server-wins
 *     (la machine d'état est canonique côté backend).
 *   - Champs synchronisés : `kind`, `text`, `signatureRequested`,
 *     `status`, `at` (dérivé de `createdAt`). Tout vient du serveur.
 *
 * No-op si le foyer n'existe pas ou n'est pas coparenting.
 */
export function applyLiaisonSyncResult(homeId: HomeId, result: LiaisonSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return;

  const data = home.data as CoparentingHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverTransmissions = result.serverTransmissions;

  // 1. Remap ids locaux migrés.
  const remappedLocal: Transmission[] = data.liaison.map((t) =>
    idMap.has(t.id) ? { ...t, id: idMap.get(t.id)! } : t,
  );

  if (!serverTransmissions) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, liaison: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const nextLiaison: Transmission[] = [];

  // 2. Server-wins par transmission. Le status (LiaisonStatus |
  //    DemandeStatus) est traité comme string côté serveur — on cast
  //    au type discriminé attendu par le shared-types ; le check
  //    SQL côté API garantit qu'il est dans le set valide.
  for (const st of serverTransmissions) {
    const merged: Transmission = {
      id: st.id,
      kind: st.kind,
      at: Date.parse(st.createdAt),
      text: st.text,
      signatureRequested: st.signatureRequested,
      status: st.status as LiaisonStatus | DemandeStatus,
    };
    nextLiaison.push(merged);
  }

  // 3. Conserve les transmissions locales non-UUID (migration ratée).
  const serverIds = new Set(serverTransmissions.map((s) => s.id));
  for (const t of remappedLocal) {
    if (!isServerHomeId(t.id) && !serverIds.has(t.id)) {
      nextLiaison.push(t);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, liaison: nextLiaison } };
  homesStore.setState({ homes: next });
}

// ── Expenses sync helpers (Phase 4.10.3) ──────────────────────────────

/**
 * Payload expense tel que retourné par `GET /homes/:homeId/expenses`.
 * Sous-entité **coparenting**. Pattern symétrique à journal (4.10.1) :
 * tous les champs viennent du serveur (la dépense est immuable une
 * fois créée — pas de PATCH).
 *
 * `amount` est un `number` côté JSON (le serveur parse depuis le
 * `numeric` SQL). `date` est un string `YYYY-MM-DD` (Drizzle `date`).
 */
export interface ServerExpensePayload {
  id: string;
  description: string;
  amount: number;
  category: string;
  paidBy: 'A' | 'B';
  date: string;
}

export interface ExpensesSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverExpenses?: readonly ServerExpensePayload[];
}

/**
 * Applique le résultat de la sync expenses pour un foyer coparenting.
 *
 * Particularités vs liaison (4.10.2) :
 *   - **Pas de mutable state** (les dépenses sont immuables). Server-wins
 *     simple par id : description, amount, category, paidBy, date.
 *   - **Mute `home.data.frais`** (champ historique du proto, en français
 *     — pas `expenses`). Cohérent avec `CoparentingHomeData.frais`.
 *
 * No-op si le foyer n'existe pas ou n'est pas coparenting.
 */
export function applyExpensesSyncResult(homeId: HomeId, result: ExpensesSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return;

  const data = home.data as CoparentingHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverExpenses = result.serverExpenses;

  // 1. Remap ids locaux migrés.
  const remappedLocal: Expense[] = data.frais.map((e) =>
    idMap.has(e.id) ? { ...e, id: idMap.get(e.id)! } : e,
  );

  if (!serverExpenses) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, frais: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const nextFrais: Expense[] = [];

  // 2. Server-wins par expense.
  for (const se of serverExpenses) {
    const merged: Expense = {
      id: se.id,
      description: se.description,
      amount: se.amount,
      // Cast au type discriminé — le CHECK SQL côté API garantit la
      // valeur dans le set valide (ExpenseCategory).
      category: se.category as ExpenseCategory,
      paidBy: se.paidBy,
      date: se.date,
    };
    nextFrais.push(merged);
  }

  // 3. Conserve les expenses locales non-UUID (migration ratée).
  const serverIds = new Set(serverExpenses.map((s) => s.id));
  for (const e of remappedLocal) {
    if (!isServerHomeId(e.id) && !serverIds.has(e.id)) {
      nextFrais.push(e);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, frais: nextFrais } };
  homesStore.setState({ homes: next });
}

// ── Bag sync helpers (Phase 4.10.4) ───────────────────────────────────

/**
 * Payload bag item tel que retourné par `GET /homes/:homeId/bag`.
 * Sous-entité **coparenting**. `status` et `location` sont **mutables
 * côté serveur** (via PATCH) — participent au merge server-wins comme
 * pour liaison (4.10.2).
 */
export interface ServerBagItemPayload {
  id: string;
  item: string;
  category: string;
  location: string;
  status: 'ok' | 'missing';
  icon: string | null;
  requiredFor: string | null;
}

export interface BagSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverItems?: readonly ServerBagItemPayload[];
}

/**
 * Applique le résultat de la sync bag pour un foyer coparenting.
 *
 * Particularités vs expenses (4.10.3) :
 *   - **`status` et `location` mutables** : server-wins inclut
 *     toute la shape, pas juste les champs immuables.
 *   - **Mute `home.data.bag`** (champ historique du proto).
 *
 * No-op si le foyer n'existe pas ou n'est pas coparenting.
 */
export function applyBagSyncResult(homeId: HomeId, result: BagSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return;

  const data = home.data as CoparentingHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverItems = result.serverItems;

  // 1. Remap ids locaux migrés.
  const remappedLocal: BagItem[] = data.bag.map((b) =>
    idMap.has(b.id) ? { ...b, id: idMap.get(b.id)! } : b,
  );

  if (!serverItems) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, bag: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const nextBag: BagItem[] = [];

  // 2. Server-wins par item. Le CHECK SQL côté API garantit que
  //    category ∈ BagCategory et status ∈ BagStatus.
  for (const si of serverItems) {
    const merged: BagItem = {
      id: si.id,
      item: si.item,
      category: si.category as BagCategory,
      location: si.location,
      status: si.status,
      ...(si.icon ? { icon: si.icon } : {}),
      ...(si.requiredFor ? { requiredFor: si.requiredFor } : {}),
    };
    nextBag.push(merged);
  }

  // 3. Conserve les items locaux non-UUID (migration ratée).
  const serverIds = new Set(serverItems.map((s) => s.id));
  for (const b of remappedLocal) {
    if (!isServerHomeId(b.id) && !serverIds.has(b.id)) {
      nextBag.push(b);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, bag: nextBag } };
  homesStore.setState({ homes: next });
}

// ── School sync helpers (Phase 4.10.5) ────────────────────────────────

/**
 * Payload school entry tel que retourné par `GET /homes/:homeId/school`.
 * Sous-entité **coparenting** — la dernière de la sous-phase 4.10.
 *
 * Pattern symétrique à journal (4.10.1) : tous les champs viennent du
 * serveur, l'entrée est immuable (pas de PATCH côté API). `type` est
 * discriminé en 3 valeurs ('note' | 'devoir' | 'info') avec CHECK SQL.
 */
export interface ServerSchoolEntryPayload {
  id: string;
  type: 'note' | 'devoir' | 'info';
  subject: string | null;
  teacher: string | null;
  comment: string | null;
  value: string | null;
  date: string;
}

export interface SchoolSyncResult {
  idMap?: ReadonlyMap<string, string>;
  serverEntries?: readonly ServerSchoolEntryPayload[];
}

/**
 * Applique le résultat de la sync school pour un foyer coparenting.
 *
 * Particularités vs autres sub-entités texte :
 *   - **Champs optionnels nullables** : subject, teacher, comment,
 *     value. Le merge applique les valeurs serveur (null inclus) sans
 *     préserver les anciennes valeurs locales (server-wins strict).
 *   - **Mute `home.data.school`** (champ historique du proto).
 *
 * No-op si le foyer n'existe pas ou n'est pas coparenting.
 */
export function applySchoolSyncResult(homeId: HomeId, result: SchoolSyncResult): void {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return;

  const data = home.data as CoparentingHomeData;
  const idMap = result.idMap ?? new Map<string, string>();
  const serverEntries = result.serverEntries;

  // 1. Remap ids locaux migrés.
  const remappedLocal: SchoolEntry[] = data.school.map((e) =>
    idMap.has(e.id) ? { ...e, id: idMap.get(e.id)! } : e,
  );

  if (!serverEntries) {
    const next = [...homes];
    next[idx] = { ...home, data: { ...data, school: remappedLocal } };
    homesStore.setState({ homes: next });
    return;
  }

  const nextSchool: SchoolEntry[] = [];

  // 2. Server-wins par entrée. Les champs optionnels nullables sont
  //    omis si null pour matcher le shape SchoolEntry shared-types
  //    (qui utilise `subject?` etc.).
  for (const se of serverEntries) {
    const merged: SchoolEntry = {
      id: se.id,
      type: se.type,
      date: se.date,
      ...(se.subject ? { subject: se.subject } : {}),
      ...(se.teacher ? { teacher: se.teacher } : {}),
      ...(se.comment ? { comment: se.comment } : {}),
      ...(se.value ? { value: se.value } : {}),
    };
    nextSchool.push(merged);
  }

  // 3. Conserve les entrées locales non-UUID (migration ratée).
  const serverIds = new Set(serverEntries.map((s) => s.id));
  for (const e of remappedLocal) {
    if (!isServerHomeId(e.id) && !serverIds.has(e.id)) {
      nextSchool.push(e);
    }
  }

  const next = [...homes];
  next[idx] = { ...home, data: { ...data, school: nextSchool } };
  homesStore.setState({ homes: next });
}

// ── API publique ──────────────────────────────────────────────────────

export function getCurrentHome(): Home | null {
  const { homes, currentHomeId } = homesStore.getState();
  return homes.find((h) => h.id === currentHomeId) ?? homes[0] ?? null;
}

export function getHomes(): readonly Home[] {
  return homesStore.getState().homes;
}

export function getCurrentHomeId(): HomeId {
  return homesStore.getState().currentHomeId;
}

/**
 * Bascule sur un foyer existant. Aucun effet si l'id est inconnu ou
 * déjà courant. Retourne `true` si la bascule a eu lieu.
 */
export function switchHome(homeId: HomeId): boolean {
  const { homes, currentHomeId } = homesStore.getState();
  if (homeId === currentHomeId) return false;
  if (!homes.some((h) => h.id === homeId)) return false;
  homesStore.setState({ currentHomeId: homeId });
  return true;
}

export interface AddHomeOptions {
  /** Couleur custom — par défaut dérivée du mode (vert sauge / bleu ardoise). */
  color?: HomeColor;
  /** Identifiants des enfants (mode co-parentage uniquement). */
  children?: HomeId[];
}

// Compteur monotone pour garantir l'unicité des ids même quand
// `Date.now()` retourne la même milliseconde sur deux appels successifs
// (cas typique en tests, et possible en navigateur sur appels rapides).
let homeIdSeq = 0;

function nextHomeId(): HomeId {
  homeIdSeq += 1;
  return `home_${Date.now().toString(36)}_${homeIdSeq.toString(36)}`;
}

/**
 * Crée et retourne un nouveau foyer. NE bascule PAS dessus automatiquement
 * (à charge de l'appelant — meilleur contrôle UX).
 */
export function addHome(name: string, mode: HomeMode, options: AddHomeOptions = {}): Home {
  const newHome: Home = {
    id: nextHomeId(),
    name,
    mode,
    color: options.color ?? COLOR_BY_MODE[mode],
    ...(options.children !== undefined ? { children: options.children } : {}),
    data: defaultDataForMode(mode),
  };
  const { homes, currentHomeId } = homesStore.getState();
  homesStore.setState({
    homes: [...homes, newHome],
    // Si c'est le premier foyer, on le rend courant.
    currentHomeId: currentHomeId === '' ? newHome.id : currentHomeId,
  });
  return newHome;
}

/**
 * Renomme un foyer existant. Le nom est trim ; ignore si vide. Retourne
 * `true` si le rename a eu lieu.
 */
export function renameHome(homeId: HomeId, newName: string): boolean {
  const trimmed = newName.trim();
  if (!trimmed) return false;
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return false;
  if (homes[idx]!.name === trimmed) return false;
  const next = [...homes];
  next[idx] = { ...next[idx]!, name: trimmed };
  homesStore.setState({ homes: next });
  return true;
}

let memberIdSeq = 0;

function nextMemberId(): MemberId {
  memberIdSeq += 1;
  return `mem_${Date.now().toString(36)}_${memberIdSeq.toString(36)}`;
}

let personIdSeq = 0;

function nextPersonId(): PersonId {
  personIdSeq += 1;
  return `per_${Date.now().toString(36)}_${personIdSeq.toString(36)}`;
}

export interface AddMemberOptions {
  /** Couleur d'avatar custom — par défaut `#7A9E8E` (vert sauge). */
  color?: string;
  /** Email optionnel (en mode local-only Phase 1, juste affiché). */
  email?: string;
}

/**
 * Ajoute un membre à un foyer existant. Le membre est local (sans `userId`
 * — c'est une entité de gestion tant que la Phase 4 n'a pas livré l'auth
 * et le flux d'invitation par email).
 *
 * Retourne le membre créé, ou `null` si le foyer n'existe pas.
 */
export function addMember(
  homeId: HomeId,
  name: string,
  role: Role,
  _options: AddMemberOptions = {},
): Member | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return null;
  const home = homes[idx]!;

  const newMember: Member = {
    id: nextMemberId(),
    homeId,
    name: trimmed,
    role,
  };

  // Mode classique : `data.members` est dans ClassicHomeData.
  // Mode co-parentage : pas de `members[]` (les enfants sont des entités
  //   séparées, et les parents sont matérialisés par CustodyConfig). On
  //   ne supporte pas l'ajout via cette API en co-parentage en Phase 1.
  if (home.mode !== 'classic') return null;
  const data = home.data as ClassicHomeData;
  const next = [...homes];
  next[idx] = { ...home, data: { ...data, members: [...data.members, newMember] } };
  homesStore.setState({ homes: next });
  return newMember;
}

/** Affectation d'une personne aux foyers (alpha, item 10c). */
export type Affectation = 'principal' | 'coparental' | 'both' | 'none';

/**
 * Ajoute une **personne** à un ou plusieurs foyers en une opération
 * atomique (alpha, item 10d). Un `personId` partagé relie les entrées.
 *
 *   - `classicHomeIds` : un `Member` réel est créé dans chaque foyer
 *     classique sélectionné (`data.members`).
 *   - `coparentingHomeIds` : le `personId` est ajouté à `Home.children`
 *     du foyer co-parentage (référence réelle, sans toucher aux
 *     structures/vues CP — cf. CLAUDE.md §2.2 « CP inchangé »).
 *
 * Retourne le `personId` créé, ou `null` si aucun foyer valide.
 */
export function addPerson(
  name: string,
  role: Role,
  affect: { classicHomeIds?: readonly HomeId[]; coparentingHomeIds?: readonly HomeId[] },
): PersonId | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const classicIds = affect.classicHomeIds ?? [];
  const coparentingIds = affect.coparentingHomeIds ?? [];
  if (classicIds.length === 0 && coparentingIds.length === 0) return null;

  const personId = nextPersonId();
  const { homes } = homesStore.getState();
  const next = homes.map((home) => {
    if (home.mode === 'classic' && classicIds.includes(home.id)) {
      const data = home.data as ClassicHomeData;
      const member: Member = { id: nextMemberId(), homeId: home.id, personId, name: trimmed, role };
      return { ...home, data: { ...data, members: [...data.members, member] } };
    }
    if (home.mode === 'coparenting' && coparentingIds.includes(home.id)) {
      const children = home.children ?? [];
      if (children.includes(personId)) return home;
      return { ...home, children: [...children, personId] };
    }
    return home;
  });
  homesStore.setState({ homes: next });
  return personId;
}

/**
 * Calcule l'affectation d'un membre aux types de foyers (item 10c) en
 * scannant tous les foyers via son `personId`. Sans `personId` (membre
 * legacy), on retombe sur le mode du foyer qui le contient.
 */
export function affectationOf(member: Member): Affectation {
  const { homes } = homesStore.getState();
  const pid = member.personId;
  if (!pid) {
    const home = homes.find((h) => h.id === member.homeId);
    return home?.mode === 'coparenting' ? 'coparental' : 'principal';
  }
  let inClassic = false;
  let inCoparenting = false;
  for (const home of homes) {
    if (home.mode === 'classic') {
      const data = home.data as ClassicHomeData;
      if (data.members.some((m) => m.personId === pid)) inClassic = true;
    } else if ((home.children ?? []).includes(pid)) {
      inCoparenting = true;
    }
  }
  if (inClassic && inCoparenting) return 'both';
  if (inCoparenting) return 'coparental';
  if (inClassic) return 'principal';
  return 'none';
}

// ── Events / Tasks (mode classique) ─────────────────────────────────

let entryIdSeq = 0;

function nextEntryId(prefix: string): string {
  entryIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${entryIdSeq.toString(36)}`;
}

function withClassicData(
  homeId: HomeId,
  mutate: (data: ClassicHomeData) => ClassicHomeData | null,
): boolean {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return false;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return false;
  const data = home.data as ClassicHomeData;
  const nextData = mutate(data);
  if (!nextData) return false;
  const next = [...homes];
  next[idx] = { ...home, data: nextData };
  homesStore.setState({ homes: next });
  return true;
}

export interface AddEventInput {
  title: string;
  date: string;
  time?: string;
  location?: string;
  who?: MemberId;
}

/**
 * Ajoute un événement au foyer classique. Trim title, refuse si vide
 * ou si la date n'est pas au format `YYYY-MM-DD`. Retourne l'événement
 * créé ou `null`.
 */
export function addEvent(homeId: HomeId, input: AddEventInput): FamilyEvent | null {
  const title = input.title.trim();
  if (!title) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) return null;
  const newEvent: FamilyEvent = {
    id: nextEntryId('ev'),
    title,
    date: input.date,
    ...(input.time ? { time: input.time } : {}),
    ...(input.location?.trim() ? { location: input.location.trim() } : {}),
    ...(input.who ? { who: input.who } : {}),
  };
  const ok = withClassicData(homeId, (data) => ({
    ...data,
    events: [...data.events, newEvent],
  }));
  return ok ? newEvent : null;
}

export function removeEvent(homeId: HomeId, eventId: string): boolean {
  return withClassicData(homeId, (data) => {
    if (!data.events.some((e) => e.id === eventId)) return null;
    return { ...data, events: data.events.filter((e) => e.id !== eventId) };
  });
}

export interface AddTaskInput {
  text: string;
  due?: string;
  who?: MemberId;
}

/**
 * Ajoute une tâche au foyer classique. Trim text, refuse si vide.
 * `done` est initialisé à `false`. Retourne la tâche créée ou `null`.
 */
export function addTask(homeId: HomeId, input: AddTaskInput): FamilyTask | null {
  const text = input.text.trim();
  if (!text) return null;
  const newTask: FamilyTask = {
    id: nextEntryId('tk'),
    text,
    done: false,
    ...(input.due?.trim() ? { due: input.due.trim() } : {}),
    ...(input.who ? { who: input.who } : {}),
  };
  const ok = withClassicData(homeId, (data) => ({
    ...data,
    tasks: [...data.tasks, newTask],
  }));
  return ok ? newTask : null;
}

/**
 * Bascule l'état done d'une tâche. Retourne `true` si la bascule a eu
 * lieu (tâche trouvée).
 */
export function toggleTask(homeId: HomeId, taskId: string): boolean {
  return withClassicData(homeId, (data) => {
    const idx = data.tasks.findIndex((t) => t.id === taskId);
    if (idx < 0) return null;
    const next = [...data.tasks];
    next[idx] = { ...next[idx]!, done: !next[idx]!.done };
    return { ...data, tasks: next };
  });
}

export function removeTask(homeId: HomeId, taskId: string): boolean {
  return withClassicData(homeId, (data) => {
    if (!data.tasks.some((t) => t.id === taskId)) return null;
    return { ...data, tasks: data.tasks.filter((t) => t.id !== taskId) };
  });
}

// ── Messages (mode classique) ───────────────────────────────────────

export interface AddMessageInput {
  /** Auteur du message — id d'un membre du foyer (obligatoire). */
  who: MemberId;
  /** Texte. Au moins l'un de `text`, `audioUrl` ou `attachmentUrl` doit être défini. */
  text?: string;
  /** URL blob ou data: pour les voicemails (Phase 1 = blob URL local). */
  audioUrl?: string;
  /** URL blob ou data: pour les pièces jointes. */
  attachmentUrl?: string;
}

/**
 * Ajoute un message au foyer classique. Refuse si le foyer n'existe pas,
 * n'est pas en mode classique, ou si le message est entièrement vide.
 * Le timestamp `at` est posé à `Date.now()`.
 *
 * Phase 2.3 : si `input.audioUrl` est fourni (cas typique : voicemail
 * issu d'un `MediaRecorder` → `URL.createObjectURL`), le blob est
 * persisté en IDB sous `voice:<msgId>` (fire-and-forget) et le
 * `message.audioUrl` du state passe au sentinel `'[idb]'`. L'URL
 * réelle reste en cache mémoire (`setVoiceUrl`) pour une lecture
 * immédiate avant le round-trip IDB — le View hydrate via
 * `getVoiceUrlNow(msgId)` aux prochains renders.
 *
 * Le champ `attachmentUrl` reste stocké tel quel pour l'instant
 * (Phase 1) — sa migration éventuelle vers IDB est traitable en
 * Phase 2.5 avec le même pattern si besoin.
 */
export function addMessage(homeId: HomeId, input: AddMessageInput): FamilyMessage | null {
  const text = input.text?.trim();
  const hasContent = !!text || !!input.audioUrl || !!input.attachmentUrl;
  if (!hasContent) return null;
  if (!input.who) return null;

  const messageId = nextEntryId('msg');

  // Voice : pose l'URL en cache mémoire + déclenche le put IDB
  // avant de muter le state, pour que le premier render qui suit
  // `subscribeHomes` trouve déjà l'URL dans le cache.
  if (input.audioUrl) {
    setVoiceUrl(messageId, input.audioUrl);
    void persistBlobFromUrl(`voice:${messageId}`, input.audioUrl);
  }

  const newMessage: FamilyMessage = {
    id: messageId,
    who: input.who,
    at: Date.now(),
    ...(text ? { text } : {}),
    ...(input.audioUrl ? { audioUrl: VOICE_IDB_SENTINEL } : {}),
    ...(input.attachmentUrl ? { attachmentUrl: input.attachmentUrl } : {}),
  };
  const ok = withClassicData(homeId, (data) => ({
    ...data,
    messages: [...data.messages, newMessage],
  }));
  if (!ok && input.audioUrl) {
    // Foyer absent ou non-classique — rollback du cache + IDB
    // pour ne pas laisser de blob orphelin.
    clearVoiceUrl(messageId);
    void deleteBlob(`voice:${messageId}`);
  }
  return ok ? newMessage : null;
}

export function removeMessage(homeId: HomeId, messageId: string): boolean {
  // Capture si le message avait un audio AVANT de muter — pour
  // décider d'un cleanup IDB ou pas.
  const { homes } = homesStore.getState();
  const home = homes.find((h) => h.id === homeId);
  const had =
    home?.mode === 'classic'
      ? (home.data as ClassicHomeData).messages.find((m) => m.id === messageId)
      : null;
  const hadAudio = had?.audioUrl === VOICE_IDB_SENTINEL;

  const removed = withClassicData(homeId, (data) => {
    if (!data.messages.some((m) => m.id === messageId)) return null;
    return { ...data, messages: data.messages.filter((m) => m.id !== messageId) };
  });
  if (removed && hadAudio) {
    clearVoiceUrl(messageId);
    void deleteBlob(`voice:${messageId}`);
  }
  return removed;
}

// ── Photos / Albums (mode classique) ────────────────────────────────

export interface AddPhotoInput {
  /** Data URL ou blob URL (Phase 1 : data:image/* / blob:). */
  img: string | null;
  /** Légende libre. */
  caption?: string;
  /** Nom de l'album dans lequel placer la photo (optionnel). */
  album?: string;
  /** Date de prise de vue (EXIF ou date du fichier) — défaut : maintenant. */
  takenAt?: number;
}

/**
 * Convertit une URL `data:` ou `blob:` en `Blob` portable.
 *
 * - Pour les `data:` URLs, on parse manuellement le base64. `fetch(dataUrl)`
 *   ne fonctionne pas de façon portable (JSDOM ne le supporte pas).
 * - Pour les `blob:` URLs, on passe par `fetch()` (vrai browser) — en
 *   environnement de test, l'appelant doit avoir mocké URL/blob ou
 *   passer une `data:` URL.
 *
 * Retourne `null` si l'URL n'est pas exploitable (format inconnu, parsing
 * échoué). L'appelant doit fallback proprement (pas de persistance IDB).
 */
async function urlToBlob(url: string): Promise<Blob | null> {
  if (url.startsWith('data:')) {
    // Parse silencieux : `addPhoto` est fire-and-forget pour la
    // persistance IDB, une data: URL malformée signifie juste « pas
    // de blob persistable » — on ne pollue pas la console.
    try {
      const commaIdx = url.indexOf(',');
      if (commaIdx < 0) return null;
      const meta = url.slice(5, commaIdx); // entre `data:` et `,`
      const payload = url.slice(commaIdx + 1);
      const isBase64 = /;base64$/i.test(meta);
      const mime = meta.replace(/;base64$/i, '').split(';')[0] || 'application/octet-stream';
      const bytes = isBase64 ? atob(payload) : decodeURIComponent(payload);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
      return new Blob([arr], { type: mime });
    } catch {
      return null;
    }
  }
  if (url.startsWith('blob:') || url.startsWith('http:') || url.startsWith('https:')) {
    try {
      const res = await fetch(url);
      return await res.blob();
    } catch (err) {
      // Cas plus rare et plus inattendu (CORS, blob: invalide) — warning
      // visible pour le dev qui débogue.
      console.warn('[homes] urlToBlob fetch failed:', err);
      return null;
    }
  }
  return null;
}

/**
 * Persiste le blob d'une URL (data: ou blob:) en IDB sous une clé
 * arbitraire. Utilisé par `addPhoto` (clé `photo:<id>`) et `addMessage`
 * pour les voicemails (clé `voice:<msgId>`). Fire-and-forget côté
 * appelant — un cache mémoire dédié sert le premier render, l'IDB
 * est uniquement pour la persistance entre sessions.
 */
async function persistBlobFromUrl(key: string, url: string): Promise<void> {
  const blob = await urlToBlob(url);
  if (!blob) return;
  await putBlob(key, blob);
}

/**
 * Ajoute une photo au foyer classique. `img` peut être null (placeholder
 * vide → fallback emoji dans l'UI), une `data:` URL (cas typique du
 * FileReader.readAsDataURL d'un input file) ou une `blob:` URL.
 *
 * Phase 2.2 : si `img` est fourni, le blob est persisté en IDB sous
 * `photo:<id>` (fire-and-forget) et le `photo.img` du store passe au
 * sentinel `'[idb]'`. L'URL réelle reste en cache mémoire
 * (`setPhotoUrl`) pour un affichage immédiat avant le round-trip IDB
 * — le View hydratera via `getPhotoUrlNow(id)` aux prochains renders.
 *
 * Refuse si le foyer n'est pas classique ou inconnu (`null`).
 */
export function addPhoto(homeId: HomeId, input: AddPhotoInput): Photo | null {
  const photoId = nextEntryId('ph');
  if (input.img) {
    // Cache mémoire — l'URL est tout de suite affichable, pas de
    // round-trip IDB pour le premier render.
    setPhotoUrl(photoId, input.img);
    // Persistance IDB — fire-and-forget : on n'attend pas pour ne
    // pas rendre `addPhoto` async (la propagation aux call sites
    // serait disproportionnée). En cas d'échec, l'image vit le
    // temps de la session (memory cache), perdue au reload.
    void persistBlobFromUrl(`photo:${photoId}`, input.img);
  }
  const newPhoto: Photo = {
    id: photoId,
    img: input.img ? PHOTO_IDB_SENTINEL : null,
    ...(input.caption?.trim() ? { caption: input.caption.trim() } : {}),
    ...(input.album?.trim() ? { album: input.album.trim() } : {}),
    takenAt: input.takenAt ?? Date.now(),
  };
  const ok = withClassicData(homeId, (data) => ({
    ...data,
    photos: [newPhoto, ...data.photos], // les plus récentes en tête
  }));
  if (!ok) {
    // Foyer absent ou non-classique — rollback du cache + IDB pour ne
    // pas laisser de blob orphelin.
    clearPhotoUrl(photoId);
    void deleteBlob(`photo:${photoId}`);
  }
  return ok ? newPhoto : null;
}

export function removePhoto(homeId: HomeId, photoId: string): boolean {
  const removed = withClassicData(homeId, (data) => {
    if (!data.photos.some((p) => p.id === photoId)) return null;
    return { ...data, photos: data.photos.filter((p) => p.id !== photoId) };
  });
  if (removed) {
    // Libère la mémoire (révoque blob: URL) puis purge IDB en parallèle.
    clearPhotoUrl(photoId);
    void deleteBlob(`photo:${photoId}`);
  }
  return removed;
}

/**
 * Ré-affecte (ou efface) l'album d'une photo SANS toucher au blob IDB.
 * Phase 2.2 : remplace l'ancien pattern `removePhoto + addPhoto` du
 * bouton ✨ IA Photos, qui cassait à cause du sentinel `'[idb]'`
 * (l'`img` passé au nouvel addPhoto n'était plus l'URL réelle).
 *
 * `albumName` à `null`/`undefined` retire l'affectation.
 */
export function setPhotoAlbum(
  homeId: HomeId,
  photoId: string,
  albumName: string | null | undefined,
): boolean {
  const trimmed = albumName?.trim() || null;
  return withClassicData(homeId, (data) => {
    const idx = data.photos.findIndex((p) => p.id === photoId);
    if (idx < 0) return null;
    const cur = data.photos[idx]!;
    if ((cur.album ?? null) === trimmed) return null;
    const next = [...data.photos];
    if (trimmed) {
      next[idx] = { ...cur, album: trimmed };
    } else {
      const { album: _omit, ...rest } = cur;
      next[idx] = rest;
    }
    return { ...data, photos: next };
  });
}

/**
 * Crée un album. Refuse les noms vides ou déjà utilisés (les albums
 * sont identifiés par leur nom, fidèle à `p.album === a.name` du proto).
 */
export function addAlbum(homeId: HomeId, name: string): Album | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const newAlbum: Album = { id: nextEntryId('al'), name: trimmed };
  let created: Album | null = null;
  withClassicData(homeId, (data) => {
    if (data.albums.some((a) => a.name === trimmed)) return null;
    created = newAlbum;
    return { ...data, albums: [...data.albums, newAlbum] };
  });
  return created;
}

/**
 * Renomme un album existant. Met aussi à jour le `album` field de chaque
 * photo qui pointait sur l'ancien nom (cohérence avec la lookup
 * `p.album === a.name` du proto).
 */
export function renameAlbum(homeId: HomeId, albumId: string, newName: string): boolean {
  const trimmed = newName.trim();
  if (!trimmed) return false;
  return withClassicData(homeId, (data) => {
    const idx = data.albums.findIndex((a) => a.id === albumId);
    if (idx < 0) return null;
    const oldName = data.albums[idx]!.name;
    if (oldName === trimmed) return null;
    if (data.albums.some((a) => a.id !== albumId && a.name === trimmed)) return null;
    const nextAlbums = [...data.albums];
    nextAlbums[idx] = { ...nextAlbums[idx]!, name: trimmed };
    const nextPhotos = data.photos.map((p) => (p.album === oldName ? { ...p, album: trimmed } : p));
    return { ...data, albums: nextAlbums, photos: nextPhotos };
  });
}

/**
 * Supprime un album. Les photos qui y étaient rattachées ne sont PAS
 * supprimées — leur champ `album` est juste effacé (port fidèle du
 * proto : suppression d'album ≠ suppression des photos).
 */
export function removeAlbum(homeId: HomeId, albumId: string): boolean {
  return withClassicData(homeId, (data) => {
    const album = data.albums.find((a) => a.id === albumId);
    if (!album) return null;
    const nextPhotos = data.photos.map((p) => {
      if (p.album !== album.name) return p;
      const { album: _omit, ...rest } = p;
      return rest;
    });
    return {
      ...data,
      albums: data.albums.filter((a) => a.id !== albumId),
      photos: nextPhotos,
    };
  });
}

// ── Meals / Grocery (mode classique) ────────────────────────────────

export interface MealAssignment {
  /** Pour le repas du midi (laisser undefined pour ne pas toucher). */
  lunch?: string;
  /** Pour le repas du soir. */
  dinner?: string;
  /** Étiquette (ex. `Familial`, `Léger`). */
  tag?: string;
  /** Nombre de portions de base. */
  bs?: number;
  /** Liste d'ingrédients (remplace l'existante). */
  ing?: { n: string; q?: number; u?: string }[];
  /** Fiche recette « à suivre » du midi (alpha). */
  lunchInfo?: MealRecipeInfo;
  /** Fiche recette « à suivre » du soir (alpha). */
  dinnerInfo?: MealRecipeInfo;
}

/**
 * Assigne un repas à un jour de la semaine (`dayIndex` 0..6, 0 = lundi
 * pour rester aligné sur l'ordre de `defaultMeals`). Les champs absents
 * du `partial` ne sont pas modifiés. Refuse si dayIndex hors plage,
 * mode coparenting ou foyer inconnu.
 */
export function setMeal(homeId: HomeId, dayIndex: number, partial: MealAssignment): boolean {
  if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex > 6) return false;
  return withClassicData(homeId, (data) => {
    const meals = [...data.meals];
    const meal = meals[dayIndex];
    if (!meal) return null;
    meals[dayIndex] = {
      ...meal,
      ...(partial.lunch !== undefined ? { lunch: partial.lunch } : {}),
      ...(partial.dinner !== undefined ? { dinner: partial.dinner } : {}),
      ...(partial.tag !== undefined ? { tag: partial.tag } : {}),
      ...(partial.bs !== undefined ? { bs: partial.bs } : {}),
      ...(partial.ing !== undefined ? { ing: partial.ing } : {}),
      ...(partial.lunchInfo !== undefined ? { lunchInfo: partial.lunchInfo } : {}),
      ...(partial.dinnerInfo !== undefined ? { dinnerInfo: partial.dinnerInfo } : {}),
    };
    return { ...data, meals };
  });
}

/** Vide un slot (remet `lunch`/`dinner`/`tag`/`ing` à des valeurs vides). */
export function clearMeal(homeId: HomeId, dayIndex: number, slot: 'lunch' | 'dinner'): boolean {
  if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex > 6) return false;
  return withClassicData(homeId, (data) => {
    const meals = [...data.meals];
    const meal = meals[dayIndex];
    if (!meal) return null;
    const updated: Meal = { ...meal, [slot]: '' };
    // Vide aussi la fiche recette du service effacé.
    if (slot === 'lunch') delete updated.lunchInfo;
    else delete updated.dinnerInfo;
    // Si les deux services sont vides, réinitialise tag + ing pour
    // ne pas garder un tag fantôme.
    if (!updated.lunch && !updated.dinner && !updated.m) {
      updated.tag = '';
      updated.ing = [];
    }
    meals[dayIndex] = updated;
    return { ...data, meals };
  });
}

export interface AddGroceryItemInput {
  text: string;
}

export function addGroceryItem(homeId: HomeId, input: AddGroceryItemInput): GroceryItem | null {
  const text = input.text.trim();
  if (!text) return null;
  const item: GroceryItem = {
    id: nextEntryId('gr'),
    text,
    done: false,
    src: 'manual',
  };
  const ok = withClassicData(homeId, (data) => ({
    ...data,
    grocery: [...data.grocery, item],
  }));
  return ok ? item : null;
}

export function toggleGroceryItem(homeId: HomeId, itemId: string): boolean {
  return withClassicData(homeId, (data) => {
    const idx = data.grocery.findIndex((g) => g.id === itemId);
    if (idx < 0) return null;
    const next = [...data.grocery];
    next[idx] = { ...next[idx]!, done: !next[idx]!.done };
    return { ...data, grocery: next };
  });
}

export function removeGroceryItem(homeId: HomeId, itemId: string): boolean {
  return withClassicData(homeId, (data) => {
    if (!data.grocery.some((g) => g.id === itemId)) return null;
    return { ...data, grocery: data.grocery.filter((g) => g.id !== itemId) };
  });
}

/**
 * Régénère la portion automatique de la liste de courses depuis les
 * repas planifiés (port fidèle de `syncGrocery` du proto l. 870 :
 * conserve les items manuels, remplace les items auto par
 * l'agrégation fraîche). Le caller est responsable d'appeler ça après
 * chaque mutation de `meals`.
 */
export function regenerateGroceryFromMeals(
  homeId: HomeId,
  builder: (meals: readonly Meal[]) => GroceryItem[],
): boolean {
  return withClassicData(homeId, (data) => {
    const manualItems = data.grocery.filter((g) => g.src === 'manual');
    const autoItems = builder(data.meals);
    return { ...data, grocery: [...autoItems, ...manualItems] };
  });
}

/**
 * Prénom du premier enfant rattaché à un foyer co-parentage (D1 :
 * « La semaine de Chloé. »). Les entrées `Home.children` sont des
 * `personId` ; le nom vit sur le membre homonyme d'un foyer classique
 * (affectation « les deux », item 10d). Sans rattachement résoluble :
 * `undefined` — l'appelant affiche un accent neutre.
 */
export function firstChildNameOf(home: Home): string | undefined {
  const ids = home.children ?? [];
  if (ids.length === 0) return undefined;
  for (const h of homesStore.getState().homes) {
    if (h.mode !== 'classic') continue;
    const member = (h.data as ClassicHomeData).members.find((m) => ids.includes(m.id));
    if (member) return member.name.split(/\s+/)[0];
  }
  return undefined;
}

/**
 * Vrai si retirer ce membre laisserait le foyer **sans aucun parent**.
 * Invariant §2.2 (audit A1) : le parent administre le foyer — un foyer
 * classique qui a un parent doit toujours en garder au moins un. La
 * garde vit au niveau du store (pas seulement de l'UI) pour qu'aucun
 * chemin de code ne puisse la contourner.
 */
export function isLastParent(homeId: HomeId, memberId: MemberId): boolean {
  const home = homesStore.getState().homes.find((h) => h.id === homeId);
  if (!home || home.mode !== 'classic') return false;
  const data = home.data as ClassicHomeData;
  const member = data.members.find((m) => m.id === memberId);
  if (!member || member.role !== 'parent') return false;
  return data.members.filter((m) => m.role === 'parent').length <= 1;
}

/**
 * Retire un membre d'un foyer (mode classique uniquement). Retourne `true`
 * si la suppression a eu lieu. Refuse de retirer le **dernier parent**
 * (invariant §2.2 — cf. `isLastParent`).
 */
export function removeMember(homeId: HomeId, memberId: MemberId): boolean {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return false;
  const home = homes[idx]!;
  if (home.mode !== 'classic') return false;
  const data = home.data as ClassicHomeData;
  if (!data.members.some((m) => m.id === memberId)) return false;
  if (isLastParent(homeId, memberId)) return false;
  const next = [...homes];
  next[idx] = {
    ...home,
    data: { ...data, members: data.members.filter((m) => m.id !== memberId) },
  };
  homesStore.setState({ homes: next });
  return true;
}

/**
 * Supprime un foyer par id. Si c'était le foyer courant, bascule sur le
 * premier restant (ou sur `''` s'il n'en reste aucun).
 */
export function removeHome(homeId: HomeId): boolean {
  const { homes, currentHomeId } = homesStore.getState();
  if (!homes.some((h) => h.id === homeId)) return false;
  const remaining = homes.filter((h) => h.id !== homeId);
  const nextCurrent = homeId === currentHomeId ? (remaining[0]?.id ?? '') : currentHomeId;
  homesStore.setState({ homes: remaining, currentHomeId: nextCurrent });
  return true;
}

/**
 * S'abonne aux changements du store homes. Retourne une fonction
 * d'unsubscribe.
 */
export function subscribeHomes(listener: (state: HomesState) => void): () => void {
  return homesStore.subscribe(listener);
}

/**
 * Helper de test : remet l'état à la valeur passée (ou vide). Ne touche
 * pas au storage côté caller.
 *
 * @internal
 */
export function _resetHomesForTests(state: Partial<HomesState> = {}): void {
  homesStore.setState(
    {
      homes: state.homes ?? [],
      currentHomeId: state.currentHomeId ?? '',
    },
    true,
  );
}

/**
 * Variante coparenting de `withClassicData` — clone immutable des
 * données d'un foyer mode `coparenting`. Refuse si le foyer n'existe
 * pas ou si son mode est `classic`.
 */
function withCoparentingData(
  homeId: HomeId,
  mutate: (data: CoparentingHomeData) => CoparentingHomeData | null,
): boolean {
  const { homes } = homesStore.getState();
  const idx = homes.findIndex((h) => h.id === homeId);
  if (idx < 0) return false;
  const home = homes[idx]!;
  if (home.mode !== 'coparenting') return false;
  const data = home.data as CoparentingHomeData;
  const nextData = mutate(data);
  if (!nextData) return false;
  const next = [...homes];
  next[idx] = { ...home, data: nextData };
  homesStore.setState({ homes: next });
  return true;
}

export interface AddActivityInput {
  title: string;
  date: string;
  time?: string;
  location?: string;
  type?: CpActivity['type'];
  parent?: 'A' | 'B';
}

/**
 * Ajoute une activité au foyer co-parentage. Trim le titre, refuse si
 * vide ou si la date n'est pas au format `YYYY-MM-DD`. Mode coparenting
 * uniquement (port de la logique du proto l. 3119). Retourne l'objet
 * créé ou `null`.
 */
export function addActivity(homeId: HomeId, input: AddActivityInput): CpActivity | null {
  const title = input.title.trim();
  if (!title) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) return null;
  const newActivity: CpActivity = {
    id: nextEntryId('cpa'),
    title,
    date: input.date,
    ...(input.time ? { time: input.time } : {}),
    ...(input.location ? { location: input.location } : {}),
    ...(input.type ? { type: input.type } : {}),
    ...(input.parent ? { parent: input.parent } : {}),
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    activities: [...data.activities, newActivity],
  }));
  return ok ? newActivity : null;
}

/**
 * Supprime une activité par id. Retourne `true` si la suppression a
 * eu lieu.
 */
export function removeActivity(homeId: HomeId, activityId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    if (!data.activities.some((a) => a.id === activityId)) return null;
    return { ...data, activities: data.activities.filter((a) => a.id !== activityId) };
  });
}

/**
 * Réassigne le parent référent d'une activité. Utile pour le swap
 * rapide depuis la liste. Retourne `true` si le swap a eu lieu.
 */
export function setActivityParent(homeId: HomeId, activityId: string, parent: 'A' | 'B'): boolean {
  return withCoparentingData(homeId, (data) => {
    const idx = data.activities.findIndex((a) => a.id === activityId);
    if (idx < 0) return null;
    const next = [...data.activities];
    next[idx] = { ...next[idx]!, parent };
    return { ...data, activities: next };
  });
}

// ── Liaison & Journal (Phase 1, incrément 15) ───────────────────────────

export interface AddJournalInput {
  text: string;
  who?: MemberId;
}

/**
 * Ajoute une entrée au journal partagé (mode coparenting). Trim le
 * texte, refuse si vide. Inserted en tête (newest first).
 */
export function addJournalEntry(homeId: HomeId, input: AddJournalInput): JournalEntry | null {
  const text = input.text.trim();
  if (!text) return null;
  const entry: JournalEntry = {
    id: nextEntryId('jr'),
    at: Date.now(),
    text,
    ...(input.who ? { who: input.who } : {}),
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    journal: [entry, ...data.journal],
  }));
  return ok ? entry : null;
}

/** Supprime une entrée du journal par id. */
export function removeJournalEntry(homeId: HomeId, entryId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    if (!data.journal.some((e) => e.id === entryId)) return null;
    return { ...data, journal: data.journal.filter((e) => e.id !== entryId) };
  });
}

export interface AddTransmissionInput {
  text: string;
  signatureRequested?: boolean;
}

/**
 * Ajoute une transmission formelle. Statut initial `brouillon`. Si
 * `signatureRequested = false`, on bascule directement en `envoyé`
 * (toujours horodaté côté status, mais pas de signatures requises).
 */
export function addTransmission(homeId: HomeId, input: AddTransmissionInput): Transmission | null {
  const text = input.text.trim();
  if (!text) return null;
  const signatureRequested = input.signatureRequested ?? false;
  const t: Transmission = {
    id: nextEntryId('lia'),
    kind: 'transmission',
    at: Date.now(),
    text,
    signatureRequested,
    status: signatureRequested ? 'brouillon' : 'envoyé',
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    liaison: [t, ...data.liaison],
  }));
  return ok ? t : null;
}

/**
 * Ajoute une demande (kind='demande'). Statut initial `en attente`.
 * Signatures non applicables aux demandes.
 */
export function addRequest(homeId: HomeId, text: string): Transmission | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const t: Transmission = {
    id: nextEntryId('req'),
    kind: 'demande',
    at: Date.now(),
    text: trimmed,
    signatureRequested: false,
    status: 'en attente',
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    liaison: [t, ...data.liaison],
  }));
  return ok ? t : null;
}

/**
 * Signe une transmission par l'un des parents. Transitions :
 *   - `brouillon` + sign A → `signé A`
 *   - `brouillon` + sign B → `signé B`
 *   - `signé A` + sign B → `signé par les deux`
 *   - `signé B` + sign A → `signé par les deux`
 *   - `envoyé` + sign A → `signé A` (le proto autorisait sign après
 *     envoi, on conserve)
 *   - état terminal `signé par les deux` : no-op (renvoie false).
 */
export function signTransmission(
  homeId: HomeId,
  transmissionId: string,
  parent: 'A' | 'B',
): boolean {
  return withCoparentingData(homeId, (data) => {
    const idx = data.liaison.findIndex((t) => t.id === transmissionId && t.kind === 'transmission');
    if (idx < 0) return null;
    const t = data.liaison[idx]!;
    const cur = t.status as LiaisonStatus;
    let next: LiaisonStatus | null = null;
    if (parent === 'A') {
      if (cur === 'signé A' || cur === 'signé par les deux') next = null;
      else if (cur === 'signé B') next = 'signé par les deux';
      else next = 'signé A';
    } else {
      if (cur === 'signé B' || cur === 'signé par les deux') next = null;
      else if (cur === 'signé A') next = 'signé par les deux';
      else next = 'signé B';
    }
    if (next === null) return null;
    const liaison = [...data.liaison];
    liaison[idx] = { ...t, status: next };
    return { ...data, liaison };
  });
}

/**
 * Met à jour le statut d'une demande (`acceptée` | `refusée`). No-op
 * si l'id ne référence pas une demande, ou si le statut est déjà ce
 * qui est demandé.
 */
export function setRequestStatus(
  homeId: HomeId,
  requestId: string,
  status: DemandeStatus,
): boolean {
  return withCoparentingData(homeId, (data) => {
    const idx = data.liaison.findIndex((t) => t.id === requestId && t.kind === 'demande');
    if (idx < 0) return null;
    const t = data.liaison[idx]!;
    if (t.status === status) return null;
    const liaison = [...data.liaison];
    liaison[idx] = { ...t, status };
    return { ...data, liaison };
  });
}

// ── Frais partagés (Phase 1, incrément 16) ──────────────────────────────

export interface AddExpenseInput {
  description: string;
  amount: number;
  category: ExpenseCategory;
  paidBy: 'A' | 'B';
  /** ISO `YYYY-MM-DD`. Default = aujourd'hui. */
  date?: string;
}

/**
 * Ajoute une dépense partagée au foyer co-parentage. Trim
 * description, refuse si vide ou montant ≤ 0 ou non fini, valide la
 * date `YYYY-MM-DD` (default = today). Mode coparenting uniquement.
 */
export function addExpense(homeId: HomeId, input: AddExpenseInput): Expense | null {
  const description = input.description.trim();
  if (!description) return null;
  if (!Number.isFinite(input.amount) || input.amount <= 0) return null;
  const date = input.date ?? new Date().toISOString().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const expense: Expense = {
    id: nextEntryId('exp'),
    description,
    amount: input.amount,
    category: input.category,
    paidBy: input.paidBy,
    date,
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    frais: [expense, ...data.frais],
  }));
  return ok ? expense : null;
}

/**
 * Supprime une dépense par id. Retourne `false` si l'id est absent.
 */
export function removeExpense(homeId: HomeId, expenseId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    if (!data.frais.some((e) => e.id === expenseId)) return null;
    return { ...data, frais: data.frais.filter((e) => e.id !== expenseId) };
  });
}

// ── Sac à dos virtuel (Phase 1, incrément 17) ───────────────────────────

export interface AddBagItemInput {
  item: string;
  category: BagCategory;
  location: string;
  icon?: string;
  /** Default `'ok'`. */
  status?: BagItem['status'];
  requiredFor?: string;
}

/**
 * Ajoute un objet au sac à dos (mode coparenting). Trim nom, refuse
 * si vide. Status par défaut = `ok`.
 */
export function addBagItem(homeId: HomeId, input: AddBagItemInput): BagItem | null {
  const item = input.item.trim();
  if (!item) return null;
  const newItem: BagItem = {
    id: nextEntryId('bg'),
    item,
    category: input.category,
    location: input.location || 'Maison',
    status: input.status ?? 'ok',
    ...(input.icon ? { icon: input.icon } : {}),
    ...(input.requiredFor ? { requiredFor: input.requiredFor } : {}),
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    bag: [...data.bag, newItem],
  }));
  return ok ? newItem : null;
}

/** Supprime un objet du sac. */
export function removeBagItem(homeId: HomeId, itemId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    if (!data.bag.some((b) => b.id === itemId)) return null;
    return { ...data, bag: data.bag.filter((b) => b.id !== itemId) };
  });
}

/** Bascule le statut `ok` ↔ `missing`. */
export function toggleBagItemStatus(homeId: HomeId, itemId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    const idx = data.bag.findIndex((b) => b.id === itemId);
    if (idx < 0) return null;
    const cur = data.bag[idx]!;
    const next: BagItem = { ...cur, status: cur.status === 'ok' ? 'missing' : 'ok' };
    const bag = [...data.bag];
    bag[idx] = next;
    return { ...data, bag };
  });
}

/**
 * « ✔ Transféré » : marque l'item `ok` ET swap sa location entre les
 * deux parents (port direct du proto l. 3563). Si le custody ou la
 * location actuelle n'est pas un nom de parent, fallback sur parentA.
 */
export function transferBagItem(
  homeId: HomeId,
  itemId: string,
  custody: CustodyConfig | null | undefined,
): boolean {
  return withCoparentingData(homeId, (data) => {
    const idx = data.bag.findIndex((b) => b.id === itemId);
    if (idx < 0) return null;
    const cur = data.bag[idx]!;
    const c = custody ?? data.custody;
    const parentA = c?.parentA ?? 'Parent A';
    const parentB = c?.parentB ?? 'Parent B';
    const nextLocation = cur.location === parentA ? parentB : parentA;
    const next: BagItem = { ...cur, status: 'ok', location: nextLocation };
    const bag = [...data.bag];
    bag[idx] = next;
    return { ...data, bag };
  });
}

/**
 * Met à jour la location d'un objet sans toucher au statut. Utilisé
 * par exemple pour un picker de location explicite.
 */
export function setBagItemLocation(homeId: HomeId, itemId: string, location: string): boolean {
  const trimmed = location.trim();
  if (!trimmed) return false;
  return withCoparentingData(homeId, (data) => {
    const idx = data.bag.findIndex((b) => b.id === itemId);
    if (idx < 0) return null;
    const cur = data.bag[idx]!;
    if (cur.location === trimmed) return null;
    const bag = [...data.bag];
    bag[idx] = { ...cur, location: trimmed };
    return { ...data, bag };
  });
}

// ── Scolarité (Phase 1, incrément 18) ───────────────────────────────────

export interface AddSchoolEntryInput {
  type: SchoolEntry['type'];
  subject?: string;
  teacher?: string;
  comment?: string;
  value?: string;
  /** ISO `YYYY-MM-DD`. Default = aujourd'hui. */
  date?: string;
}

/**
 * Ajoute une entrée scolaire (note / devoir / info). En mode coparenting
 * uniquement. Newest-first via unshift (cohérent avec proto l. 3142).
 */
export function addSchoolEntry(homeId: HomeId, input: AddSchoolEntryInput): SchoolEntry | null {
  const date = input.date ?? new Date().toISOString().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const entry: SchoolEntry = {
    id: nextEntryId('sc'),
    type: input.type,
    date,
    ...(input.subject ? { subject: input.subject.trim() } : {}),
    ...(input.teacher ? { teacher: input.teacher.trim() } : {}),
    ...(input.comment ? { comment: input.comment.trim() } : {}),
    ...(input.value ? { value: input.value.trim() } : {}),
  };
  const ok = withCoparentingData(homeId, (data) => ({
    ...data,
    school: [entry, ...data.school],
  }));
  return ok ? entry : null;
}

/** Supprime une entrée scolaire par id. */
export function removeSchoolEntry(homeId: HomeId, entryId: string): boolean {
  return withCoparentingData(homeId, (data) => {
    if (!data.school.some((e) => e.id === entryId)) return null;
    return { ...data, school: data.school.filter((e) => e.id !== entryId) };
  });
}

// ── Custody config (Phase 1, incrément 18) ──────────────────────────────

export interface UpdateCustodyInput {
  parentA?: string;
  parentB?: string;
  /** ISO `YYYY-MM-DD`. */
  startDate?: string;
  weekParity?: 0 | 1;
}

/**
 * Met à jour la config de garde du foyer co-parentage. Trim les noms,
 * valide le format de date si fourni. Refuse si aucun champ ne change.
 */
export function updateCustodyConfig(homeId: HomeId, input: UpdateCustodyInput): boolean {
  return withCoparentingData(homeId, (data) => {
    const cur = data.custody;
    const next: CustodyConfig = { ...cur };
    let changed = false;
    if (input.parentA !== undefined) {
      const t = input.parentA.trim();
      if (t && t !== cur.parentA) {
        next.parentA = t;
        changed = true;
      }
    }
    if (input.parentB !== undefined) {
      const t = input.parentB.trim();
      if (t && t !== cur.parentB) {
        next.parentB = t;
        changed = true;
      }
    }
    if (input.startDate !== undefined) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(input.startDate)) return null;
      if (input.startDate !== cur.startDate) {
        next.startDate = input.startDate;
        changed = true;
      }
    }
    if (input.weekParity !== undefined && input.weekParity !== cur.weekParity) {
      next.weekParity = input.weekParity;
      changed = true;
    }
    if (!changed) return null;
    return { ...data, custody: next };
  });
}

/** @internal pour tests — purge le storage. */
export function _clearHomesStorageForTests(): void {
  const storage = safeLocalStorage();
  if (storage) storage.removeItem(STORAGE_KEY_HOMES);
}

/**
 * @internal pour tests — rejoue le chemin de boot (lecture du storage,
 * désérialisation + migrations comprises). Permet de tester la
 * migration des rôles legacy (audit B2) sur le vrai code de chargement.
 */
export function _reloadHomesFromStorageForTests(): void {
  const loaded = loadFromStorage(safeLocalStorage());
  homesStore.setState(loaded ?? { homes: [], currentHomeId: '' }, true);
}
